import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CustomValidators } from '@core-lib/classes/custom-validators.class';
import { StoreLoader } from '@core-lib/classes/store-loader.class';
import { ActionType } from '@core-lib/enums/action-type.enum';
import { markFormGroupTouched } from '@core-lib/functions';
import { IAction } from '@core-lib/models/action.model';
import { ICommentaireCreateForm, IOperationCommentaireCreateForm } from '@core-lib/models/commentaire.model';
import { UploadDocumentModalComponent } from '@core-lib/modules/document-ged/components/upload-document-modal/upload-document-modal.component';
import { DocumentGedApiService } from '@core-lib/modules/document-ged/services/document-ged-api.service';
import { DocumentGedService } from '@core-lib/modules/document-ged/services/document-ged.service';
import { CommentService } from '@core-lib/services/comment.service';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { ToastrService } from 'ngx-toastr';
import { IOperationFilesForm } from 'projects/app-front/src/app/affaires-module/models';
import { IOperationRenvoyer } from 'projects/app-front/src/app/alertes-module/models/alertes.model';
import { Subject, of } from 'rxjs';
import { DocumentSupportedType } from '@core-lib/enums';

@Component({
    selector: 'commentaires-form',
    templateUrl: './commentaires-form.component.html',
    standalone: false
})
export class CommentairesFormComponent implements OnInit {
	form: UntypedFormGroup;
	@Output() action: EventEmitter<IAction> = new EventEmitter();
	@Input() operation: IOperationRenvoyer;
	@Input() mouvementBancaire: any; // TODO: IVirementRecu deplacer dans le coreLib

	loader: StoreLoader = new StoreLoader(of(false), of(null));
	loaderSubject = new Subject<StoreLoader>();
	loader$ = this.loaderSubject.asObservable();

	get commentaireField(): AbstractControl {
		return this.form?.get('commentaire');
	}

	get documentsField(): AbstractControl {
		return this.form?.get('documents');
	}

	files: any[] = [];
	modalRef: ModalRef;
	constructor(
		private commentService: CommentService,
		private documentGedApiService: DocumentGedApiService,
		private modalService: ModalService,
		private documentGedService: DocumentGedService,
		private toastr: ToastrService
	) {}

	ngOnInit(): void {
		this.form = new UntypedFormGroup(
			{
				// Validation: l'utilisateur doit fournir un message (ajout minimum d'un commentaire OU ajout d'une PJ) pour pouvoir envoyer sa réponse via le picto Image sinon message d'erreur suivant "Vous devez ajouter un commentaire et/ou une pièce jointe"
				commentaire: new UntypedFormControl(null),
				documents: new UntypedFormGroup({
					filesList: new UntypedFormArray([])
				})
			},
			[CustomValidators.requiredCommentOrDocument()]
		);

		this.loader$.subscribe(res => (this.loader = res));

		// Inscription à la liste des documents téléchargés
		this.documentGedService.getDataForm().subscribe(res => {
			// res: cet objet contient les fichiers
			if (res) {
				this.documentsField.patchValue(res);
			}
		});
	}

	onSubmit(): void {
		let formValue = this.form.getRawValue();
		// Commentaire sur une opération
		if(this.operation){
			if (this.documentsField?.value?.filesList?.length < 1 && (!this.commentaireField.value || this.commentaireField.value === '')) {
				markFormGroupTouched(this.form);
				return;
			}
	
			let commentaire = formValue as IOperationCommentaireCreateForm;
			commentaire.sousMotifId = this.operation?.sousMotif?.id;
			commentaire.operationId = this.operation?.operationId;
	
			this.setLoader(true);
			// Création de formulaire
			this.commentService
				.createCommentaire(this.operation?.affaireId, this.operation?.operationId, commentaire)
				.pipe()
				.subscribe({
					next: (commentaireId: string) => {
						this.setLoader(false);
						if (formValue?.documents?.filesList?.length > 0) {
							this.setLoader(true);
	
							let filesForm: IOperationFilesForm;
							filesForm = {
								operationId: this.operation?.operationId,
								documents: formValue?.documents,
								affaireId: this.operation?.affaireId,
								commentaireId: commentaireId
							};
	
							this.documentGedApiService
								.uploadOperationFileCommentaire(filesForm)
								.pipe()
								.subscribe({
									next: res => {
										this.setLoader(false);
										if (res.errors?.length > 0) {
											res.errors.forEach(errorMsg => {
												this.toastr.error(errorMsg, 'Erreur');
											});
										}
										this.action.emit({
											type: ActionType.Submit
										});
									},
									error: error => {
										this.toastr.error(error.message, 'Erreur');
										this.setLoader(false);
										this.action.emit({
											type: ActionType.Submit
										});
									}
								});
						} else {
							this.action.emit({
								type: ActionType.Submit
							});
						}
					},
					error: error => {
						this.setLoader(false, error);
					}
				});
		}
		else if(this.mouvementBancaire)
		{
			if (!this.commentaireField.value || this.commentaireField.value === '') {
				markFormGroupTouched(this.form);
				return;
			}
			let commentaire = formValue as ICommentaireCreateForm;
			this.setLoader(true);
			this.commentService
				.createCommentaireMouvementBancaire(this.mouvementBancaire?.id, commentaire)
				.pipe()
				.subscribe({
					next: (commentaireId: string) => {
						this.setLoader(false);
						this.action.emit({
							type: ActionType.Submit
						});
						this.form?.reset();
					},
					error: error => {
						this.setLoader(false, error);
					}
				});
		}
		
	}

	//#region Upload documents
	/**
	 * Action pour l'ouverture du modal de dépôt de fichier
	 * @param e Evenement sur le click
	 */
	handleOpenUploadDocumentsModal(e: MouseEvent): void {
		e.preventDefault();
		// Open modal upload document
		this.modalRef = this.modalService.show(UploadDocumentModalComponent, {
			ignoreBackdropClick: true,
			class: 'modal-xl'
		});

		// Id operation
		this.modalRef.content.operationId = this.operation?.operationId;
		this.modalRef.content.documentType = DocumentSupportedType.Operation;
		this.modalRef.content.scannerFileName = `${this.operation?.numeroAffaire}_${this.operation.typeOperation}_${this.operation?.montant}`;
		// Liste des fichiers
		this.modalRef.content.form = this.documentsField;
	}

	/**
	 * Suppression d'un fichier ajouté'
	 * @param index
	 */
	deleteFile(index: number): void {
		let filesListFormArray = this.documentsField.get('filesList') as UntypedFormArray;
		filesListFormArray.removeAt(index);
	}
	//#endregion Upload documents

	setLoader(isLoading: boolean, error = null): void {
		this.loaderSubject.next(new StoreLoader(of(isLoading), of(error)));
	}
}
